'use client'
import { useState } from 'react';
import Footer from '@/components/Footer';
import usePhoneNumber from '@/hooks/usePhoneNumber';
import 'swiper/css';
import 'swiper/css/pagination';
import MainMenu from '@/components/MainMenu';
import { checkTokenSendRequest } from '@/utils/apiService';

type Scores = {
  extraversion: number;
  agreeableness: number;
  conscientiousness: number;
  emotionalStability: number;
  opennessToExperience: number;
};

const traitDescriptions: { [key in keyof Scores]: string } = {
  extraversion: "Таны нийгмийн болон харилцааны чадварын түвшин.",
  agreeableness: "Таны бусадтай эв найртай байх хандлага.",
  conscientiousness: "Таны хариуцлагатай, сахилга баттай байдал.",
  emotionalStability: "Сэтгэл хөдлөлөө хянах чадвар.",
  opennessToExperience: "Таны шинэ туршлага, санаа хүлээн авах хандлага."
};

export default function Page() {
  const { phoneNumber, isCookieLoading } = usePhoneNumber();
  const [responses, setResponses] = useState<number[]>(Array(10).fill(0));
  const [error, setError] = useState<number | null>(null);
  const [scores, setScores] = useState<Scores | null>(null);
  const [loading, setLoading] = useState<boolean>(false);

  const handleResponseChange = (index: number, value: number) => {
    const newResponses = [...responses];
    newResponses[index] = value;
    setResponses(newResponses);

    if (error === index) {
      setError(null);
    }

    const nextQuestion = document.getElementById(`question-${index + 1}`);
    if (nextQuestion) {
      nextQuestion.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    const firstUnansweredIndex = responses.findIndex(response => response === 0);
    if (firstUnansweredIndex !== -1) {
      setError(firstUnansweredIndex);
      const questionElement = document.getElementById(`question-${firstUnansweredIndex}`);
      if (questionElement) {
        questionElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    } else if(!loading) {
		setLoading(true);
      	calculateScores().finally(()=>{setLoading(false)})
    }
  };

  const calculateScores = async () => {
	checkTokenSendRequest('POST', '/api/free/tipi/', { fill: responses });
    setScores({
		extraversion: (responses[0] + (7 - responses[5])) / 2,
		agreeableness: ((7 - responses[1]) + responses[6]) / 2,
		conscientiousness: (responses[2] + (7 - responses[7])) / 2,
		emotionalStability: ((7 - responses[3]) + responses[8]) / 2,
		opennessToExperience: (responses[4] + (7 - responses[9])) / 2,
	});
  };


  const traitTexts: {
    [key in keyof Scores]: { range: [number, number]; text: string }[];
  } = {
    extraversion: [
      { range: [0, 2.99], text: "Хүнтэй харилцах сонирхол багатай. Цагийг аль болох ганцаараа өнгөрүүлэхийг илүүд үздэг. Хувийн орон зайгаа их эрхэмлэдэг учраас өөрийнхөө талаар нэг их ярьдаггүй, үзэл бодлоо ч төдийлөн хуваалцдаггүй. Тиймдээ ч олон нийтийг хамарсан үйл ажиллагаа, нийллэг зэрэгт оролцох, багаар ажиллахаас илүүтэй ганцаараа аль эсвэл цөөхүүлээ байх нь таатай санагддаг." },
      { range: [3, 4.4], text: "Хүнтэй харилцах сонирхол их биш. Цагийг ганцаараа өнгөрүүлэхийг илүүд үздэг. Хувийн орон зайгаа эрхэмлэдэг учраас өөрийнхөө талаар тэр бүр яриад байдаггүй. Тиймдээ ч олон нийтийг хамарсан үйл ажиллагаа, нийллэг зэрэгт оролцох, багаар ажиллахаас ганцаараа аль эсвэл цөөхүүлээ байхыг илүүд үздэг." },
      { range: [4.5, 5.8], text: "Хүмүүстэй харилцах сонирхол өндөртэй. Хүмүүстэй цагийг өнгөрөөж, шинэ хүмүүстэй танилцахыг эрмэлздэг. Үдэшлэг нийллэг зохион байгуулах, бусадтай зугаацах, хүмүүсийг инээлгэх, олон нийтийн ажилд оролцох дуртай." },
      { range: [5.9, 7.7], text: "Хүмүүстэй харилцах сонирхол өндөртэй. Цагийг аль болох олон хүмүүстэй өнгөрөөж, шинэ, содон хүмүүстэй танилцахыг эрмэлздэг. Үдэшлэг нийллэг зохион байгуулах, бусадтай зугаацах, хүмүүсийг инээлгэх, олон нийтийн ажилд оролцох ихээхэн дуртай." }
    ],
    agreeableness: [
      { range: [0, 4.1], text: "Бусадтай өрсөлдөх дуртай, хүмүүстэй хатуу, шулуун байдлаар харьцдаг. Бие даасан байдлаар шийдвэр гаргадаг бөгөөд бусдын нөлөөнд автах, хүмүүсийн заавар доор байх дургүй. Өөрт төрсөн сэтгэгдлээ шулуухан илэрхийлдэг ба үзэл бодол, дэвшүүлсэн санаагаа хамгаалж, тэмцдэг бөгөөд бууж өгөх дургүй." },
      { range: [4.2, 5.2], text: "Бусадтай өрсөлдөх дуртай, хүмүүстэй хатуу, шулуун байдлаар харьцдаг. Бие даасан байдлаар шийдвэр гаргадаг бөгөөд бусдын нөлөөнд автах, хүмүүсийн заавар доор байх дургүй. Өөрт төрсөн сэтгэгдлээ шулуухан илэрхийлдэг ба үзэл бодол, дэвшүүлсэн санаагаа хамгаалж, тэмцэж чаддаг." },
      { range: [5.3, 6.3], text: "Эелдэг зөөлөн харилцаатай, эв найрамдалтай байдлыг чухалчилдгаараа онцлог. Харилцаж буй хүмүүсийнхээ нөхцөл байдал, сэтгэл зүйг ойлгохыг эрхэмлэдэг тул дийлэнхдээ бусдад хүлцэнгүй, дуулгавартай, тайван, тэвчээртэй ханддаг." },
      { range: [6.4, 7], text: "Маш эелдэг зөөлөн харилцаатай, эв найрамдалтай байдлыг хадгалахыг хичээдгээрээ онцлог. Харилцаж буй хүмүүсийнхээ нөхцөл байдал, сэтгэл зүйг ойлгохыг чухалчилдаг тул бусдад нэлээд хүлцэнгүй, дуулгавартай, тайван, тэвчээртэй байдлаар ханддаг. Бусдад таагүй сэтгэгдэл төрүүлэхгүй байхад их анхаардаг." }
    ],
    conscientiousness: [
      { range: [0, 4], text: "Үүрэг хариуцлага, үр бүтээмжээс илүү одоо цагтаа амьдрах, тайван тааваараа байхыг чухалчилдаг. Алдаа дутагдал гаргах, хариуцлага алдах зэрэгт өөртөө болон бусдад их шүүмжлэлтэй хандаж буруу өгдөггүй. " },
      { range: [4.1, 5.4], text: "Үүрэг хариуцлага, үр бүтээмжээс илүү одоо цагтаа амьдрах, тайван тааваараа байхыг илүүд үздэг. Алдаа дутагдал гаргах, хариуцлага алдах зэрэгт өөртөө болон бусдад тэр бүр шүүмжлэлтэй хандаж буруутгаад байдаггүй. " },
      { range: [5.5, 6.72], text: "Оногдсон үүрэг даалгаврыг цаг тухайд нь ном журмын дагуу ягштал гүйцэтгэхийг эрмэлздэг. Цагийг хий дэмий, үр ашиггүй өнгөрүүлэхийг хүсдэггүй учир байнга ямар нэг зүйл хийж завгүй байхыг зорьдог." },
      { range: [6.7, 7], text: "Оногдсон үүрэг даалгаврыг цагx тухайд нь ном журмын дагуу ягштал гүйцэтгэхэд онцгойлон анхаардаг. Цагийг хий дэмий, үр ашиггүй өнгөрүүлэхийг хүсдэггүй учир байнга ямар нэг зүйл хийж завгүй байхыг зорьдог бөгөөд өөртөө их ачаалал өгөх талтай." }
    ],
    emotionalStability: [
      { range: [0, 3.4], text: "Өнгөрсөн дэх алдаа, одоо цагийн асуудлууд болон ирээдүйн тодорхойгүй байдлын талаар тэр бүр бодож санаа зовнидоггүй. Урт хугацааныд үргэлжлэх, төвөгтэй асуудалтай нүүр тулгараагүй бол баргийн зүйлд зовниж, сандарч, түгшдэггүй. Хүнд асуудал тулгарсан ч гэлээ богино хугацаанд дасан зохицож, богино хугацаанд даван туулдаг." },
      { range: [3.5, 4.8], text: "Өнгөрсөн дэх алдаа, одоо цагийн асуудлууд болон ирээдүйн тодорхойгүй байдлын талаар төдийлөн бодож санаа зовнидоггүй. Урт хугацааны, тогтвортой, төвөгтэй асуудалтай нүүр тулгараагүй бол хэр баргийн зүйл зовниж, сандарч, түгшдэггүй. Хүнд асуудал тулгарсан ч гэлээ дороо дасан зохицож, хүндрэл багатай даван туулдаг." },
      { range: [4.9, 6.2], text: "Өнгөрсөнд болсон явдал, одоо гаргасан алдаа болон ирээдүйн тодорхойгүй байдал зэрэгт санаа зовж, түгшиж явдаг. Ямар нэгэн асуудал тулгарсан эсвэл тэр талаар бодох төдийд л сандарч, түгшиж, зовниж эхэлдэг. Амархан сэтгэлээр унах, хямрах хандлагатай." },
      { range: [6.2, 7], text: "Өнгөрсөнд болсон явдал, одоо гаргасан алдаа болон ирээдүйн тодорхойгүй байдал зэрэгт их санаа зовж, түгшиж явдаг. Ямар нэгэн асуудал тулгарсан эсвэл тэр талаар бодох төдийд л сандарч, түгшиж, зовниж эхэлдэг. Жижигхэн асуудалд ч сэтгэлээр амархан унах, хямрах байдлаар хариу үзүүлэх талтай." }
    ],
    opennessToExperience: [
      { range: [0, 4.3], text: "Шинэлэг зүйлээс илүүтэй уламжлалыг хадгалахыг хичээдгээрээ онцлог. Ер нь шинийг санаачлахаасаа илүүтэй тодорхой, тогтсон арга барилаар аливаад ханддаг. Хийх зүйл нь тодорхой, ойлгомжтой нөхцөл байдалд ажиллах дуртай. Амьдралд ямар нэг хэмжээгээр өөрчлөлт, шинэчлэлт бий болоход ихээхэн тухгүй санагддаг." },
      { range: [4.4, 5.3], text: "Шинэлэг зүйлээс илүүтэй уламжлалыг хадгалж үлдэх сонирхолтой байдгаараа онцлог. Ер нь шинийг санаачлахаасаа илүүтэй тодорхой, тогтсон арга барилаар юмсыг хийх дуртай. Хийх зүйл нь тодорхой, ойлгомжтой нөхцөл байдалд ажиллахыг илүүд үздэг. Амьдралд ямар нэг хэмжээгээр өөрчлөлт, шинэчлэлт бий болоход бага зэрэг тухгүй санагддаг." },
      { range: [5.4, 6.4], text: "Өөрчлөлт хийж, аливааг сайжруулахыг боддог. Шинийг сурах сонирхол өндөр бөгөөд шинэ мэдлэг, чадварт суралцах эрмэлзэл өндөртэйн зэрэгцээ сонирхсон зүйлийнхээ араас ухаж судалдаг. Онолын, философилог асуудлууд эсвэл урлагийн чиглэлийн бүтээлч үйл ажиллагаанд сонирхол татагддаг." },
      { range: [6.5, 7], text: "Аливаа зүйлд өөрчлөлт хийж, сайжруулахыг хичээдэг. Шинэ, тодорхойгүй орчинд хялбархан дасан зохицож чаддаг. Шинийг сурах сонирхол маш өндөр бөгөөд байнга шинэ мэдлэг, чадварт суралцахын зэрэгцээ сонирхсон зүйлийнхээ араас ухаж судалдаг. Онолын, философилог асуудлууд эсвэл урлагийн чиглэлийн бүтээлч үйл ажиллагаанд дуртай." }
    ]
  };

  const getTraitText = (trait: keyof Scores, score: number) => {
    const traitText = traitTexts[trait].find(({ range }) => score >= range[0] && score <= range[1]);
    return traitText ? traitText.text : "";
  };
  

  return (
    <main className="flex min-h-screen flex-col items-center justify-between overflow-hidden">
      <MainMenu isLoading={isCookieLoading} phoneNumber={phoneNumber} />
      <section className="text-start pt-10 pb-0 md:py-24 w-screen justify-start bg-gradient-to-b from-gradient-start to-gradient-start  dark:from-purple-700 dark:to-dark-800 min-h-[80vh]">
        <div className="container my-20 max-w-[700px] mx-auto">
          <div className="space-y-10">
            <div className="text-lg space-y-4">
              {!scores && (
                <>
                  <div className="text-3xl md:text-4xl font-semibold text-center">TIPI тест</div>
                  <p>Доор таныг илэрхийлж болох өөр өөр зан чанаруудыг бичсэн байгаа. Тухайн зан чанар тус бүр таныг оновчтой илэрхийлж байгаа эсэхийг үнэлээрэй.</p>
                  <p>1 = Огт санал нийлэхгүй байна, 7 = Бүрэн санал нийлж байна</p>
                  <form onSubmit={handleSubmit} className="text-center pt-10">
                    {[
                      "Нээлттэй, урам зоригтой",
                      "Шүүмжлэнгүй, маргаанч",
                      "Найдвартай, сахилга баттай",
                      "Түгшүүртэй, амархан хямардаг",
                      "Шинэ зүйлсийг нээлттэй хүлээж авдаг, ойлгоход хэцүү",
                      "Чимээгүй, дуу цөөнтэй",
                      "Өрөвч, халуун дотно",
                      "Зохион байгуулалтгүй, болгоомжгүй",
                      "Тайван, сэтгэл хөдлөлийн хувьд тогтвортой",
                      "Уламжлалт, бүтээлч биш"
                    ].map((item, index) => (
                      <div
                        key={index}
                        id={`question-${index}`}
                        className={`space-y-4 mb-10 text-center p-4 rounded-lg bg-white dark:bg-dark-600 shadow-sm ${
                          responses[index] === 0 ? 'opacity-100' : 'opacity-60'
                        }`}
                      >
                        <label className="font-medium text-xl">{item}</label>
                        <div className="flex space-x-4 mt-2 justify-center">
                          {Array.from({ length: 7 }, (_, i) => i + 1).map(value => (
                            <label key={value} className="flex flex-col items-center cursor-pointer">
                              <input
                                type="radio"
                                name={`item-${index}`}
                                value={value}
                                checked={responses[index] === value}
                                onChange={() => handleResponseChange(index, value)}
                                className="cursor-pointer appearance-none w-10 h-10 border-2 bg-white border-gray-300 rounded-full checked:bg-purple-500 checked:border-purple-500 focus:outline-none"
                              />
                              <span className="mt-1 text-lg font-semibold">{value}</span>
                            </label>
                          ))}
                        </div>
                        {error === index && (
                          <p className="text-red-500 text-sm mt-2">Энэ асуултанд хариулна уу</p>
                        )}
                      </div>
                    ))}
                    <button type="submit" className="mt-6 px-6 py-3 bg-purple-600 hover:bg-purple-700 text-white rounded-lg text-lg">
                      Үр дүн харах
                    </button>
                  </form>
                </>
              )}
              {scores && (
                <div className="mt-10 text-center">
                  <div className="text-3xl md:text-4xl font-semibold text-center mb-4">Таны үзүүлэлт</div>
                  {Object.entries(scores).map(([trait, score]) => (
                    <div key={trait} className="mb-8">
                      <p className="text-lg font-semibold mb-2">{traitDescriptions[trait as keyof Scores]}</p>
                      <div className="w-full bg-gray-300 rounded-full h-6">
                        <div
                          className="bg-purple-500 h-6 rounded-full"
                          style={{ width: `${(score / 7) * 100}%` }}
                        ></div>
                      </div>
                      {/* <div className="text-lg font-semibold pt-1">{score}/7</div> */}
                      <p className="mt-2">{getTraitText(trait as keyof Scores, score)}</p>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </section>
      <Footer />
    </main>
  );
}
